import { memo, useEffect, useRef, useState } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import type { GaugeConfigEntry } from "../types/config";
import type { TelemetryValue } from "../types/telemetry";
import { isTelemetryStale } from "../lib/telemetryFreshness";
import { resolveThemeColor } from "../lib/themeColors";

interface RollingLineGraphProps {
  config: GaugeConfigEntry;
  telemetry?: TelemetryValue;
}

const HISTORY_SECONDS = 30;
// 4 Hz is visually smooth over a 30 s window and quarters uPlot.setData load
// across nine gauges compared with the previous 100 ms tick.
const PLOT_TICK_MS = 250;

function getZoneColor(value: number, fallbackColor: string, zones?: {from: number; to: number; color: string}[]) {
  if (!zones || zones.length === 0) return fallbackColor;
  const z = zones.find(z => value >= z.from && value <= z.to);
  return z ? z.color : fallbackColor;
}

function RollingLineGraph({ config, telemetry }: RollingLineGraphProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const plotRef = useRef<uPlot | null>(null);
  const strokeColorRef = useRef<string>("#8b95b0");
  const dataRef = useRef<[number[], number[]]>([[], []]);
  const readoutRef = useRef<HTMLSpanElement>(null);
  
  const telemetryRef = useRef<TelemetryValue | undefined>(telemetry);
  const [stale, setStale] = useState(false);
  // Assigned during render (not an effect) so the header value and the
  // sampling interval both see the current prop immediately; an effect
  // would leave a freshly-mounted graph stuck on "--" until the next
  // telemetry update re-renders it.
  const latestValue = telemetry?.value ?? NaN;
  telemetryRef.current = telemetry;

  useEffect(() => {
    if (!containerRef.current) return;

    const gridColor = resolveThemeColor("--border", "rgba(255,255,255,0.12)", containerRef.current);
    const textColor = resolveThemeColor("--text-dim", "#a0a0a0", containerRef.current);
    const accentColor = resolveThemeColor("--accent", "#f59e0b", containerRef.current);
    strokeColorRef.current = accentColor;
    const fillColor = resolveThemeColor("--chart-fill", "rgba(245, 158, 11, 0.1)", containerRef.current);

    const opts: uPlot.Options = {
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
      cursor: {
        show: true,
        y: false,
        drag: { x: false, y: false, setScale: false },
      },
      legend: { show: false },
      hooks: {
        setCursor: [
          (u) => {
            const readout = readoutRef.current;
            if (!readout) return;
            const idx = u.cursor.idx;
            const xs = u.data[0];
            const ys = u.data[1];
            const y = idx != null ? ys[idx] : null;
            if (idx == null || y == null || isNaN(y)) {
              readout.textContent = "";
              return;
            }
            const secondsAgo = Date.now() / 1000 - xs[idx];
            readout.textContent = `${y.toFixed(config.decimals ?? 0)} ${config.unit} · ${secondsAgo.toFixed(1)}s ago`;
          },
        ],
      },
      axes: [
        {
          show: false,
        },
        {
          stroke: textColor,
          font: "10px JetBrains Mono, monospace",
          size: 40,
          grid: { stroke: gridColor, width: 1 },
          values: (_u, vals) => vals.map(v => v.toFixed(config.decimals ?? 0))
        }
      ],
      scales: {
        x: {
          time: false,
          auto: false,
          range: () => {
            const now = Date.now() / 1000;
            return [now - HISTORY_SECONDS, now];
          }
        },
        y: {
          auto: false,
          range: [config.min, config.max],
        }
      },
      series: [
        {},
        {
          // uPlot wraps stroke into a function at construction; reassigning a
          // string later throws inside every redraw. Read the live zone colour
          // through a ref instead.
          stroke: () => strokeColorRef.current,
          width: 2,
          fill: fillColor,
        }
      ],
      padding: [10, 10, 10, 10]
    };

    plotRef.current = new uPlot(opts, [[], []], containerRef.current);

    const ro = new ResizeObserver(() => {
      if (plotRef.current && containerRef.current) {
        plotRef.current.setSize({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        });
      }
    });
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      if (plotRef.current) {
        plotRef.current.destroy();
        plotRef.current = null;
      }
    };
  }, [config.min, config.max, config.decimals, config.unit]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now() / 1000;
      const isStale = isTelemetryStale(telemetryRef.current);
      setStale(isStale);
      // Plot a gap (NaN) while stale so a dead feed doesn't render as a
      // steady line of repeated last values.
      const value = isStale ? NaN : (telemetryRef.current?.value ?? NaN);
      
      const [xs, ys] = dataRef.current;
      xs.push(now);
      ys.push(value);

      const cutoff = now - HISTORY_SECONDS;
      let shiftCount = 0;
      while (shiftCount < xs.length && xs[shiftCount] < cutoff) {
        shiftCount++;
      }
      if (shiftCount > 0) {
        xs.splice(0, shiftCount);
        ys.splice(0, shiftCount);
      }

      if (plotRef.current) {
        if (!isNaN(value)) {
          const fallback = resolveThemeColor("--text-dim", "#8b95b0", containerRef.current);
          strokeColorRef.current = getZoneColor(value, fallback, config.zones);
        }
        plotRef.current.setData([xs, ys]);
      }
    }, PLOT_TICK_MS);

    return () => clearInterval(interval);
  }, [config.zones]);

  const displayValue = isNaN(latestValue) ? "--" : latestValue.toFixed(config.decimals ?? 0);
  // Using var() for DOM node styles is safe without resolution
  const color = getZoneColor(latestValue, "var(--text-dim)", config.zones);

  return (
    <div className={`gauge${stale ? " gauge--stale" : ""}`}>
      <div className="gauge__header">
        <div className="gauge__label">
          {config.label}
          {stale && <span className="gauge-stale-badge">STALE</span>}
          <span ref={readoutRef} className="gauge-cursor-readout" />
        </div>
        <div className="gauge__value" style={{ color }}>
          {displayValue} <span className="gauge__unit">{config.unit}</span>
        </div>
      </div>
      <div className="gauge__plot" ref={containerRef} />
    </div>
  );
}

export default memo(RollingLineGraph);
